import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import Webcam from "react-webcam";
import { Loader, Text, Button, useMantineTheme } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { isMobile } from "react-device-detect";
import useDocumentDetection, { resolveCaptureMode } from "../../../hooks/useDocumentDetection";
import useLocale from "../../../hooks/useLocale";
import AndroidCameraBlockPortal from "../../../components/AndroidCameraBlockPortal";
import { getAndroidBlockVariant, shouldShowAndroidCameraBlock } from "../../../utils/androidDetection";
import { trackEvent } from "../../../utils/telemetry";
import { ReactComponent as VeratadLogo } from "../../../assets/veratad_ai_white_logo_large.svg";
import { IconCameraOff } from "@tabler/icons-react";
import { useDeclarePortraitOnly } from "../../../hooks/useOrientation";

// JPEG quality for the uploaded ID images (was 0.97, fallbacks 1.0).
// Matches the Microblink path: the Aug-Sep 2026 upload-compression
// A/B at 0.87 cut upload size 73% and median upload time 51% with
// PASS rate unchanged-to-better. Smaller files also shorten the
// encode itself, which the Android v2 shutter waits on.
const ID_DOC_JPEG_QUALITY = 0.87;
const STYLE_ID = "veratad-doc-capture-styles";

function injectStyles(glowColor) {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    :root { --doc-glow-color: ${glowColor}; }

    @keyframes docGlowPulse {
      0%, 100% {
        box-shadow:
          0 0 calc(6px * var(--doc-glow-intensity)) calc(2px * var(--doc-glow-intensity)) var(--doc-glow-color),
          0 0 calc(18px * var(--doc-glow-intensity)) calc(5px * var(--doc-glow-intensity)) var(--doc-glow-color);
        opacity: calc(0.3 + 0.14 * var(--doc-glow-intensity));
      }
      50% {
        box-shadow:
          0 0 calc(12px * var(--doc-glow-intensity)) calc(4px * var(--doc-glow-intensity)) var(--doc-glow-color),
          0 0 calc(30px * var(--doc-glow-intensity)) calc(10px * var(--doc-glow-intensity)) var(--doc-glow-color);
        opacity: calc(0.4 + 0.12 * var(--doc-glow-intensity));
      }
    }

    @keyframes docGlowLocked {
      0% {
        box-shadow:
          0 0 calc(14px * var(--doc-glow-intensity)) calc(4px * var(--doc-glow-intensity)) var(--doc-glow-color),
          0 0 calc(35px * var(--doc-glow-intensity)) calc(10px * var(--doc-glow-intensity)) var(--doc-glow-color);
      }
      100% {
        box-shadow:
          0 0 calc(10px * var(--doc-glow-intensity)) calc(3px * var(--doc-glow-intensity)) var(--doc-glow-color),
          0 0 calc(25px * var(--doc-glow-intensity)) calc(8px * var(--doc-glow-intensity)) var(--doc-glow-color);
      }
    }

    @keyframes badgeFadeIn {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes captureFlash {
      0%   { opacity: 1; }
      100% { opacity: 0; }
    }

    /* Hide native video play button / controls on mobile */
    video::-webkit-media-controls,
    video::-webkit-media-controls-panel,
    video::-webkit-media-controls-play-button,
    video::-webkit-media-controls-start-playback-button {
      display: none !important;
      -webkit-appearance: none !important;
    }
  `;
  document.head.appendChild(style);
}

function removeStyles() {
  const el = document.getElementById(STYLE_ID);
  if (el) el.remove();
}

// Corner bracket SVG component
function CornerBrackets({ x, y, width, height, color, detected }) {
  const bracketLen = 24;
  const strokeWidth = 3;
  const opacity = detected ? 1 : 0.4;
  const c = detected ? color : "rgba(255,255,255,0.5)";

  return (
    <g style={{ transition: "opacity 0.3s" }} opacity={opacity}>
      {/* Top-left */}
      <polyline
        points={`${x},${y + bracketLen} ${x},${y} ${x + bracketLen},${y}`}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Top-right */}
      <polyline
        points={`${x + width - bracketLen},${y} ${x + width},${y} ${x + width},${y + bracketLen}`}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Bottom-left */}
      <polyline
        points={`${x},${y + height - bracketLen} ${x},${y + height} ${x + bracketLen},${y + height}`}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Bottom-right */}
      <polyline
        points={`${x + width - bracketLen},${y + height} ${x + width},${y + height} ${x + width},${y + height - bracketLen}`}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

// Analyze a video element for passport indicators (MRZ + aspect ratio).
// Uses the provided canvas ref to avoid creating a new canvas each call.
function analyzePassportFromVideo(video, canvasRef) {
  try {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    const aspectRatio = vw / vh;
    const aspectRatioPassport = aspectRatio >= 1.3 && aspectRatio <= 1.5;

    let canvas = canvasRef.current;
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvasRef.current = canvas;
    }
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, vw, vh);

    // Scan bottom 30% for MRZ bands
    const mrzTop = Math.floor(vh * 0.7);
    const mrzHeight = vh - mrzTop;
    const imageData = ctx.getImageData(0, mrzTop, vw, mrzHeight);
    const pixels = imageData.data;

    const darkThreshold = 80;
    const minDarkPercent = 0.35;
    let denseRowCount = 0;
    let bandCount = 0;
    let inBand = false;

    for (let row = 0; row < mrzHeight; row++) {
      let darkPixels = 0;
      for (let col = 0; col < vw; col++) {
        const idx = (row * vw + col) * 4;
        const luma = pixels[idx] * 0.299 + pixels[idx + 1] * 0.587 + pixels[idx + 2] * 0.114;
        if (luma < darkThreshold) darkPixels++;
      }
      if (darkPixels / vw >= minDarkPercent) {
        if (!inBand) { bandCount++; inBand = true; }
        denseRowCount++;
      } else {
        inBand = false;
      }
    }

    const mrzDetected = bandCount >= 2 && denseRowCount >= 10;

    return {
      isPassport: mrzDetected || aspectRatioPassport,
      mrzDetected,
      aspectRatioPassport,
      aspectRatio: aspectRatio.toFixed(3),
      bandCount,
      denseRowCount,
    };
  } catch {
    return null;
  }
}

export default function DocumentCapture({
  onCapture,
  onCaptureStarted,
  onError,
  onReady,
  onCameraAllowed,
  side = "front",
  captureSettings = {},
  paused = false,
  isBrowserHandoffSession = false,
  preAcquiredStream = null,
  requestId = null,
  onPhotoFallback,
}) {
  // Not laid out for landscape: keeps the rotate prompt up here even
  // under a landscape preview flag (see OrientationWarning).
  useDeclarePortraitOnly();
  const theme = useMantineTheme();
  const locale = useLocale("document-capture");
  // Stamped onto every camera/capture telemetry event so provider and
  // capture mode are queryable on the whole funnel, not just uploads.
  const telemetryBase = { provider: "veratad", mode: resolveCaptureMode(captureSettings), capture_ui: "v1" };
  const { width: vw, height: vh } = useViewportSize();
  const webcamRef = useRef(null);
  const videoElRef = useRef(null); // plain <video> ref when using pre-acquired stream
  const passportCanvasRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [androidOsBlock, setAndroidOsBlock] = useState(false);
  const cameraRespondedRef = useRef(false);
  const retryAttemptedRef = useRef(false);

  // When using a pre-acquired stream, we render a plain <video> instead of
  // react-webcam (which always calls getUserMedia internally and can't accept
  // an existing stream). We shim webcamRef so the rest of the code that uses
  // webcamRef.current.video and webcamRef.current.getScreenshot() keeps working.
  useEffect(() => {
    if (!preAcquiredStream) return;

    const attach = () => {
      const video = videoElRef.current;
      if (!video) {
        requestAnimationFrame(attach);
        return;
      }
      console.log("[Capture] Attaching pre-acquired stream to plain <video>");
      // The parent already ran getUserMedia successfully to get this stream,
      // so this is the "granted" signal on the pre-acquired path.
      trackEvent("id_doc_camera_permission_granted", { ...telemetryBase }, { once: true });
      video.srcObject = preAcquiredStream;
      video.play().catch(() => {});

      // Shim webcamRef so existing code (handleCapture, detection, etc.) works
      webcamRef.current = {
        video,
        getScreenshot: () => {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext("2d").drawImage(video, 0, 0);
          return canvas.toDataURL("image/jpeg", ID_DOC_JPEG_QUALITY);
        },
      };

      cameraRespondedRef.current = true;
      if (onCameraAllowed) onCameraAllowed();
      const checkReady = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
          trackEvent("id_doc_camera_ready", { ...telemetryBase, width: video.videoWidth, height: video.videoHeight, side });
          setCameraReady(true);
        } else {
          requestAnimationFrame(checkReady);
        }
      };
      requestAnimationFrame(checkReady);
    };
    requestAnimationFrame(attach);

    return () => {
      preAcquiredStream.getTracks().forEach((t) => t.stop());
    };
  }, [preAcquiredStream]);
  const [passportInfo, setPassportInfo] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const primaryColor =
    theme.colors[theme.primaryColor]?.[5] || theme.colors.blue[5];

  // Check permissions API if available — for handoff sessions, detect if
  // camera is already blocked so we can show unblock instructions instead
  // of trying and silently failing.
  useEffect(() => {
    console.log("[Capture] Component MOUNTED, starting camera flow");
    trackEvent("id_doc_camera_requested", { ...telemetryBase }, { once: true });
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "camera" })
        .then((status) => {
          console.log("[Capture] Permission API state:", status.state);
          const showAndroidBlock = shouldShowAndroidCameraBlock();
          if (status.state === "denied" && (isBrowserHandoffSession || showAndroidBlock)) {
            console.warn("[Capture] Camera pre-denied — showing unblock instructions", showAndroidBlock ? "(Android OS block likely)" : "");
            cameraRespondedRef.current = true;
            setCameraBlocked(true);
            if (showAndroidBlock) setAndroidOsBlock(true);
            removeStyles();

            window.dispatchEvent(new CustomEvent("veratad-camera-error"));

            if (onErrorRef.current) {
              onErrorRef.current({
                type: "camera_error",
                name: "NotAllowedError",
                message: "Permission denied",
              });
            }
            if (typeof window.__veratadIdDocCameraError === "function") {
              window.__veratadIdDocCameraError();
            }
          }
          status.onchange = () => {
            console.log("[Capture] Permission state CHANGED to:", status.state);
          };
        })
        .catch((err) => {
          console.log("[Capture] Permission API not supported:", err.message);
        });
    }
  }, []);

  // Timeout: if getUserMedia neither resolves nor rejects (e.g. iOS Safari
  // "Cancel" dismisses the prompt without firing either callback), treat it
  // as a camera error so the UI doesn't hang indefinitely.
  // Use a longer timeout for browser handoff sessions — the user is switching
  // apps and the permission prompt may take longer to appear.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const cameraTimeoutMs = isBrowserHandoffSession ? 15000 : 7000;

  useEffect(() => {
    console.log(`[Capture] Timeout effect registered (${cameraTimeoutMs / 1000}s)`);
    const timer = setTimeout(() => {
      console.log(`[Capture] ${cameraTimeoutMs / 1000}s timeout fired — cameraResponded:`, cameraRespondedRef.current, "cameraReady:", cameraReady);
      if (!cameraRespondedRef.current) {
        console.warn("[Capture] Camera permission timeout — no response after timeout");
        trackEvent("id_doc_camera_permission_timeout", { ...telemetryBase });

        setCameraFailed(true);
        removeStyles();

        window.dispatchEvent(new CustomEvent("veratad-camera-error"));

        if (onErrorRef.current) {
          onErrorRef.current({
            type: "camera_error",
            name: "TimeoutError",
            message: "Camera permission prompt timed out",
          });
        }
        if (typeof window.__veratadIdDocCameraError === "function") {
          window.__veratadIdDocCameraError();
        }
      }
    }, cameraTimeoutMs);
    return () => clearTimeout(timer);
  }, []);

  // Lifecycle logging + cleanup
  useEffect(() => {
    console.log("[Capture] MOUNT side:", side);
    return () => {
      console.log("[Capture] UNMOUNT side:", side);
      // Free reusable canvases
      if (passportCanvasRef.current) {
        passportCanvasRef.current.width = 0;
        passportCanvasRef.current.height = 0;
        passportCanvasRef.current = null;
      }
      if (captureCanvasRef.current) {
        captureCanvasRef.current.width = 0;
        captureCanvasRef.current.height = 0;
        captureCanvasRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    console.log("[Capture] Props changed — side:", side, "paused:", paused, "cameraReady:", cameraReady);
  }, [side, paused, cameraReady]);

  const {
    modelsLoaded,
    modelLoadError,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    documentType,
    confidence,
    boundingBox,
    showManualCapture,
    setOnAutoCapture,
    getFrameChain,
    areaRatio,
    captured,
    yoloDetail,
    captureStrategy,
    yoloMissCount,
  } = useDocumentDetection(webcamRef, !paused && cameraReady, captureSettings, side);

  const isStupidMode = captureStrategy === "stupid";
  const feedbackMessage = locale?.feedback?.[feedbackKey] || feedbackKey;

  // Count passing gates for glow intensity
  const passingCount = Object.values(gatingState).filter(Boolean).length;

  // Document guide dimensions (ID card aspect ratio 1.586:1)
  const guideWidth = isMobile ? Math.min(vw * 0.92, 400) : 360;
  const guideHeight = guideWidth / 1.586;
  const guideX = (vw - guideWidth) / 2;
  const guideY = (vh - guideHeight) / 2 - (isMobile ? 60 : 30);
  const guideRadius = 12;

  // Inject/remove CSS
  useEffect(() => {
    injectStyles(primaryColor);
    return removeStyles;
  }, [primaryColor]);

  // Notify parent when models + camera are ready
  useEffect(() => {
    if (modelsLoaded && cameraReady && onReady) {
      onReady();
    }
  }, [modelsLoaded, cameraReady, onReady]);

  const handleCapture = useCallback(() => {
    if (!webcamRef.current) return;
    try {
      const video = webcamRef.current.video;
      if (!video) return;

      // Notify parent synchronously BEFORE async toBlob — this lets
      // the parent set a guard ref so camera errors during the async
      // window won't reset the flow to setup.
      if (onCaptureStarted) onCaptureStarted();

      const vWidth = video.videoWidth;
      const vHeight = video.videoHeight;

      const frameChain = getFrameChain();
      if (vWidth > 0 && vHeight > 0) {
        // Reuse a dedicated canvas ref for capture to avoid leaking canvases
        let canvas = captureCanvasRef.current;
        if (!canvas) {
          canvas = document.createElement("canvas");
          captureCanvasRef.current = canvas;
        }

        // In stupid mode, crop to the guide box region so the ID fills
        // the output image instead of being a small portion of the full frame.
        if (isStupidMode && vw > 0 && vh > 0) {
          const videoAspect = vWidth / vHeight;
          const viewAspect = vw / vh;

          let offsetX, offsetY, scale;
          if (videoAspect > viewAspect) {
            // Video is wider than viewport — cropped on left/right
            const visibleW = vHeight * viewAspect;
            offsetX = (vWidth - visibleW) / 2;
            offsetY = 0;
            scale = vHeight / vh;
          } else {
            // Video is taller than viewport — cropped on top/bottom
            const visibleH = vWidth / viewAspect;
            offsetX = 0;
            offsetY = (vHeight - visibleH) / 2;
            scale = vWidth / vw;
          }

          // Add 15% padding around the guide box so we capture some
          // margin beyond the exact edges (accounts for slight misalignment)
          const padX = guideWidth * 0.15;
          const padY = guideHeight * 0.15;
          const cropX = Math.round(offsetX + (guideX - padX) * scale);
          const cropY = Math.round(offsetY + (guideY - padY) * scale);
          const cropW = Math.round((guideWidth + padX * 2) * scale);
          const cropH = Math.round((guideHeight + padY * 2) * scale);

          // Clamp to video bounds
          const clampedX = Math.max(0, Math.min(cropX, vWidth));
          const clampedY = Math.max(0, Math.min(cropY, vHeight));
          const clampedW = Math.min(cropW, vWidth - clampedX);
          const clampedH = Math.min(cropH, vHeight - clampedY);

          canvas.width = clampedW;
          canvas.height = clampedH;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, clampedX, clampedY, clampedW, clampedH, 0, 0, clampedW, clampedH);
        } else {
          // Non-stupid mode or fallback: capture full frame
          canvas.width = vWidth;
          canvas.height = vHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, vWidth, vHeight);
        }

        // Use toBlob instead of toDataURL — saves ~33% memory
        canvas.toBlob(
          (blob) => {
            if (blob && onCapture) {
              trackEvent("id_doc_captured", { ...telemetryBase, side, doc_type: documentType });
              const previewUrl = URL.createObjectURL(blob);
              onCapture(blob, previewUrl, documentType, confidence, frameChain);
            }
          },
          "image/jpeg",
          ID_DOC_JPEG_QUALITY
        );
      } else {
        // Fallback: use react-webcam screenshot, convert data URL to blob directly
        // NOTE: Do NOT use fetch(dataUrl) — see face-capture/capture.js for details
        const imageSrc = webcamRef.current.getScreenshot();
        if (imageSrc && onCapture) {
          try {
            const [header, b64] = imageSrc.split(",");
            const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: mime });
            trackEvent("id_doc_captured", { ...telemetryBase, side, doc_type: documentType, fallback: "screenshot" });
            const previewUrl = URL.createObjectURL(blob);
            onCapture(blob, previewUrl, documentType, confidence, frameChain);
          } catch (e) {
            if (onError) onError({ type: "capture_error", message: "Failed to convert screenshot to blob" });
          }
        }
      }
    } catch (err) {
      if (onError)
        onError({
          type: "capture_error",
          message: err?.message || "Screenshot capture failed",
        });
    }
  }, [onCapture, onCaptureStarted, onError, documentType, confidence, getFrameChain, isStupidMode, side, vw, vh, guideX, guideY, guideWidth, guideHeight]);

  // Register auto-capture callback
  useEffect(() => {
    setOnAutoCapture(handleCapture);
  }, [handleCapture, setOnAutoCapture]);

  // Periodic passport analysis on live video (debug) — uses ref canvas to avoid leaking
  useEffect(() => {
    if (!cameraReady || isStupidMode) return;
    const interval = setInterval(() => {
      const video = webcamRef.current?.video;
      if (video && video.videoWidth > 0) {
        setPassportInfo(analyzePassportFromVideo(video, passportCanvasRef));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [cameraReady]);

  const handleUserMedia = useCallback(() => {
    console.log("[Capture] handleUserMedia fired — camera stream received");
    trackEvent("id_doc_camera_permission_granted", { ...telemetryBase }, { once: true });
    cameraRespondedRef.current = true;
    const video = webcamRef.current?.video;
    if (!video) return;
    if (onCameraAllowed) onCameraAllowed();
    const checkReady = () => {
      if (
        video.videoWidth > 0 &&
        video.videoHeight > 0 &&
        video.readyState >= 2
      ) {
        trackEvent("id_doc_camera_ready", { ...telemetryBase, width: video.videoWidth, height: video.videoHeight, side });
        setCameraReady(true);
      } else {
        requestAnimationFrame(checkReady);
      }
    };
    requestAnimationFrame(checkReady);
  }, [onCameraAllowed, side]);

  const handleUserMediaError = useCallback(
    (err) => {
      console.error("[Capture] handleUserMediaError fired:", err?.name, err?.message, err);
      cameraRespondedRef.current = true;

      // NotReadableError means the camera is locked by another app (e.g. the
      // original webview that triggered the handoff). Retry once after a short
      // delay to give the other process time to release the camera.
      if (err?.name === "NotReadableError" && !retryAttemptedRef.current) {
        retryAttemptedRef.current = true;
        console.log("[Capture] NotReadableError — retrying getUserMedia in 2s");
        setTimeout(() => {
          cameraRespondedRef.current = false;
          navigator.mediaDevices
            .getUserMedia({
              video: {
                facingMode: "environment",
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
            })
            .then((stream) => {
              console.log("[Capture] Retry succeeded — attaching stream");
              trackEvent("id_doc_camera_permission_granted", { ...telemetryBase }, { once: true });
              cameraRespondedRef.current = true;
              const video = webcamRef.current?.video;
              if (video) {
                video.srcObject = stream;
                video.play().catch(() => {});
                const checkReady = () => {
                  if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
                    trackEvent("id_doc_camera_ready", { ...telemetryBase, width: video.videoWidth, height: video.videoHeight, side });
                    setCameraReady(true);
                    if (onCameraAllowed) onCameraAllowed();
                  } else {
                    requestAnimationFrame(checkReady);
                  }
                };
                requestAnimationFrame(checkReady);
              }
            })
            .catch((retryErr) => {
              console.error("[Capture] Retry also failed:", retryErr?.name, retryErr?.message);
              trackEvent("id_doc_camera_permission_denied", { ...telemetryBase, name: retryErr?.name, error: retryErr?.message, retried: true });
              cameraRespondedRef.current = true;
              setCameraFailed(true);
              removeStyles();
              window.dispatchEvent(new CustomEvent("veratad-camera-error"));
              try {
                if (onError) {
                  onError({
                    type: "camera_error",
                    name: retryErr?.name || "UnknownError",
                    message: retryErr?.message || "Camera access failed",
                    constraint: retryErr?.constraint || null,
                  });
                }
              } catch (e) {
                console.error("[Capture] onError callback threw:", e);
              }
              if (typeof window.__veratadIdDocCameraError === "function") {
                window.__veratadIdDocCameraError();
              }
            });
          return;
        }, 2000);
        return;
      }

      trackEvent("id_doc_camera_permission_denied", { ...telemetryBase, name: err?.name, error: err?.message });

      // On Android, a NotAllowedError without user interaction likely means
      // the OS-level Camera toggle is blocking. Show specific instructions.
      if (shouldShowAndroidCameraBlock() && err?.name === "NotAllowedError") {
        console.warn("[Capture] Android NotAllowedError — likely OS camera toggle or Chrome permission blocked");
        setCameraBlocked(true);
        setAndroidOsBlock(true);
        removeStyles();

        window.dispatchEvent(new CustomEvent("veratad-camera-error"));
        try {
          if (onError) {
            onError({
              type: "camera_error",
              name: err?.name,
              message: err?.message || "Camera access blocked by device",
              constraint: err?.constraint || null,
            });
          }
        } catch (e) {
          console.error("[Capture] onError callback threw:", e);
        }
        if (typeof window.__veratadIdDocCameraError === "function") {
          window.__veratadIdDocCameraError();
        }
        return;
      }

      // Self-close: show error portal
      setCameraFailed(true);
      removeStyles();

      // Dispatch DOM event — most reliable way to reach the parent
      window.dispatchEvent(new CustomEvent("veratad-camera-error"));

      // Also try props callback and global as additional channels
      try {
        if (onError) {
          onError({
            type: "camera_error",
            name: err?.name || "UnknownError",
            message: err?.message || "Camera access failed",
            constraint: err?.constraint || null,
          });
        }
      } catch (e) {
        console.error("[Capture] onError callback threw:", e);
      }

      if (typeof window.__veratadIdDocCameraError === "function") {
        window.__veratadIdDocCameraError();
      }
    },
    [onError, onCameraAllowed, side]
  );

  // Android camera block: route to the shared portal which handles variant
  // Camera-fallback portal. Now renders for all devices via the generic
  // variant — Android users see the Quick Settings / Chrome permission
  // instructions below the primary button, non-Android users just see
  // the primary camera-app button and refresh. The state name
  // `androidOsBlock` is legacy from when this was Android-only.
  if (androidOsBlock) {
    return (
      <AndroidCameraBlockPortal
        locale={locale}
        requestId={requestId}
        captureType="id_doc"
        isBrowserHandoffSession={isBrowserHandoffSession}
        androidVariant={getAndroidBlockVariant()}
        onPhotoFallback={onPhotoFallback}
      />
    );
  }

  // Generic browser-level camera blocked (non-Android handoff sessions).
  if (cameraBlocked) {
    const blockedPortal = (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          backgroundColor: "#000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <IconCameraOff size={64} color="rgba(255,255,255,0.7)" stroke={1.5} />
        <Text c="white" fz={22} fw={700} align="center" mt="lg">
          {locale?.errors?.cameraBlockedTitle || "Camera Access Blocked"}
        </Text>
        <Text c="rgba(255,255,255,0.7)" fz={14} align="center" mt="sm" maw={320}>
          {locale?.errors?.cameraBlockedMessage ||
            "Camera access is blocked in your browser. To enable it:"}
        </Text>
        <div style={{ textAlign: "left", maxWidth: 300, marginTop: 16 }}>
          <Text c="rgba(255,255,255,0.85)" fz={14} mb={8}>
            {locale?.errors?.cameraBlockedStep1 || "1. Tap the lock or settings icon in your browser's address bar"}
          </Text>
          <Text c="rgba(255,255,255,0.85)" fz={14} mb={8}>
            {locale?.errors?.cameraBlockedStep2 || "2. Find Camera permissions"}
          </Text>
          <Text c="rgba(255,255,255,0.85)" fz={14} mb={8}>
            {locale?.errors?.cameraBlockedStep3 || "3. Change to \"Allow\""}
          </Text>
          <Text c="rgba(255,255,255,0.85)" fz={14}>
            {locale?.errors?.cameraBlockedStep4 || "4. Come back and tap the button below"}
          </Text>
        </div>
        <Button
          variant="white"
          color="dark"
          size="md"
          mt="xl"
          onClick={() => {
            window.location.reload();
          }}
        >
          {locale?.errors?.tryAgainAfterUnblock || "Try Again"}
        </Button>
      </div>
    );
    return ReactDOM.createPortal(blockedPortal, document.body);
  }

  // Camera error: show error screen inside a portal
  if (cameraFailed) {
    const errorPortal = (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          backgroundColor: "#000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <IconCameraOff size={64} color="rgba(255,255,255,0.7)" stroke={1.5} />
        <Text c="white" fz={22} fw={700} align="center" mt="lg">
          {locale?.errors?.cameraErrorTitle || "Camera Access Denied"}
        </Text>
        <Text c="rgba(255,255,255,0.7)" fz={14} align="center" mt="sm" maw={320}>
          {locale?.errors?.cameraDenied ||
            "You have denied camera permissions. Please go into settings and allow camera, refresh the page, and try again."}
        </Text>
        <Button
          variant="white"
          color="dark"
          size="md"
          mt="xl"
          onClick={() => {
            setCameraFailed(false);
            window.dispatchEvent(new CustomEvent("veratad-camera-error"));
            try {
              if (onErrorRef.current) {
                onErrorRef.current({
                  type: "camera_error",
                  name: "NotAllowedError",
                  message: "Camera access denied by user",
                });
              }
            } catch (e) {
              console.error("[Capture] onError threw:", e);
            }
            if (typeof window.__veratadIdDocCameraError === "function") {
              window.__veratadIdDocCameraError();
            }
          }}
        >
          {locale?.errors?.goBack || "Go Back"}
        </Button>
      </div>
    );
    return ReactDOM.createPortal(errorPortal, document.body);
  }

  // Document type badge text
  const classificationBadge = documentType
    ? locale?.classification?.[documentType] || documentType.replace("_", " ")
    : null;

  const portal = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
        backgroundColor: "#000",
        overflow: "hidden",
      }}
    >
      {/* Loading indicator with debug status */}
      {(!cameraReady || (!isStupidMode && !modelsLoaded)) && (
        <div
          style={{
            position: "absolute",
            left: guideX,
            top: guideY,
            width: guideWidth,
            height: guideHeight,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            pointerEvents: "none",
            zIndex: 15,
          }}
        >
          <Loader size={36} color="white" />
          <Text
            fw={500}
            c="white"
            fz={14}
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}
          >
            {isStupidMode
              ? (locale?.capture?.loadingCamera || "Starting camera...")
              : (locale?.capture?.loadingModels || "Loading document scanner...")}
          </Text>
          {!isStupidMode && (
            <div
              style={{
                fontSize: 10,
                fontFamily: "monospace",
                color: "rgba(255,255,255,0.7)",
                textAlign: "center",
                lineHeight: 1.8,
                textShadow: "0 1px 3px rgba(0,0,0,0.9)",
              }}
            >
              <div>Camera: {cameraReady ? "\u2705" : "\u23F3 waiting..."}</div>
              <div>YOLO model: {modelsLoaded ? (modelLoadError ? "\u26A0\uFE0F failed" : "\u2705") : "\u23F3 loading..."}</div>
            </div>
          )}
        </div>
      )}

      {/* Camera layer — use plain <video> when pre-acquired stream is
          available, otherwise use react-webcam. react-webcam always calls
          getUserMedia internally so it can't accept an existing stream. */}
      {preAcquiredStream ? (
        <video
          ref={videoElRef}
          autoPlay
          playsInline
          muted
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: cameraReady && (isStupidMode || modelsLoaded) ? 1 : 0,
            transition: "opacity 0.3s ease-in",
          }}
        />
      ) : (
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          screenshotQuality={ID_DOC_JPEG_QUALITY}
          playsInline
          videoConstraints={{
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            aspectRatio: { ideal: 16 / 9 },
          }}
          onUserMedia={handleUserMedia}
          onUserMediaError={handleUserMediaError}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: cameraReady && (isStupidMode || modelsLoaded) ? 1 : 0,
            transition: "opacity 0.3s ease-in",
          }}
        />
      )}

      {/* SVG overlay with rounded-rect cutout + corner brackets */}
      <svg
        width={vw}
        height={vh}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      >
        <defs>
          <mask id="doc-rect-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={guideX}
              y={guideY}
              width={guideWidth}
              height={guideHeight}
              rx={guideRadius}
              ry={guideRadius}
              fill="black"
            />
          </mask>
        </defs>

        {/* Dark overlay with rect cutout */}
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.85)"
          mask="url(#doc-rect-mask)"
        />

        {/* Guide border */}
        <rect
          x={guideX}
          y={guideY}
          width={guideWidth}
          height={guideHeight}
          rx={guideRadius}
          ry={guideRadius}
          fill="none"
          stroke={
            passingCount === 0 ? "rgba(255,255,255,0.5)" : primaryColor
          }
          strokeWidth={isAllPassing ? 4 : passingCount >= 4 ? 3.5 : 2.5}
          style={{ transition: "stroke 0.4s, stroke-width 0.4s" }}
        />

        {/* Corner brackets */}
        <CornerBrackets
          x={guideX}
          y={guideY}
          width={guideWidth}
          height={guideHeight}
          color={primaryColor}
          detected={gatingState.documentPresent}
        />
      </svg>

      {/* Glow div */}
      {(() => {
        const glowIntensity = passingCount === 0 ? 1 : passingCount + 1;
        const glowColorValue =
          passingCount === 0 ? "rgba(255,255,255,0.4)" : primaryColor;
        return (
          <div
            style={{
              position: "absolute",
              left: guideX,
              top: guideY,
              width: guideWidth,
              height: guideHeight,
              borderRadius: guideRadius,
              background: "transparent",
              pointerEvents: "none",
              zIndex: 10,
              "--doc-glow-intensity": glowIntensity,
              "--doc-glow-color": glowColorValue,
              animation: isAllPassing
                ? "docGlowLocked 1.5s ease-in-out infinite alternate"
                : "docGlowPulse 2s ease-in-out infinite",
              transition: "opacity 0.4s",
            }}
          />
        );
      })()}

      {/* Document type badge */}
      {cameraReady && modelsLoaded && classificationBadge && (
        <div
          style={{
            position: "absolute",
            top: guideY - 44,
            left: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 20,
            animation: "badgeFadeIn 0.4s ease-out",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "#fff",
              padding: "6px 14px",
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>{classificationBadge}</span>
            {documentType === "passport" && (
              <span
                style={{
                  fontSize: 11,
                  opacity: 0.8,
                  fontWeight: 400,
                }}
              >
                {locale?.classification?.frontOnly || "— Front side only"}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Feedback text */}
      <div
        style={{
          position: "absolute",
          top: guideY + guideHeight + 30,
          left: 0,
          width: "100%",
          textAlign: "center",
          pointerEvents: "none",
          zIndex: 20,
        }}
      >
        <span
          style={{
            color: "#fff",
            fontSize: isMobile ? 16 : 18,
            fontWeight: 500,
            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
          }}
        >
          {captured ? "Captured! Uploading..." : (cameraReady && (isStupidMode || modelsLoaded) ? (isStupidMode ? (locale?.capture?.manualFeedback || "Center your document and tap the button") : feedbackMessage) : "")}
        </span>
      </div>

      {/* Capture button — stupid mode: prominent button near guide box */}
      {isStupidMode && cameraReady && !captured && (
        <div
          style={{
            position: "absolute",
            top: guideY + guideHeight + 70,
            left: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 20,
          }}
        >
          <button
            onClick={handleCapture}
            style={{
              padding: "14px 44px",
              borderRadius: 30,
              border: "none",
              backgroundColor: primaryColor,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
              letterSpacing: "0.02em",
            }}
          >
            {locale?.capture?.captureButton || "Capture"}
          </button>
        </div>
      )}

      {/* Help icon + manual capture (non-stupid mode) */}
      {!isStupidMode && cameraReady && modelsLoaded && !captured && (
        <div
          style={{
            position: "absolute",
            bottom: isMobile ? 68 : 60,
            left: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            zIndex: 20,
          }}
        >
          {/* Manual capture button — shown after help tap or 10s timeout */}
          {(helpOpen || showManualCapture) && (
            <button
              onClick={handleCapture}
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                border: "4px solid white",
                backgroundColor: "rgba(255,255,255,0.2)",
                cursor: "pointer",
                transition: "background-color 0.2s, opacity 0.3s",
              }}
              onMouseOver={(e) =>
                (e.target.style.backgroundColor = "rgba(255,255,255,0.4)")
              }
              onMouseOut={(e) =>
                (e.target.style.backgroundColor = "rgba(255,255,255,0.2)")
              }
            />
          )}

          {/* Help / lifejacket icon */}
          {!helpOpen && !showManualCapture && (
            <button
              onClick={() => setHelpOpen(true)}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.6)",
                backgroundColor: "rgba(255,255,255,0.15)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(255,255,255,0.8)",
                fontSize: 18,
                fontWeight: 700,
                fontFamily: "serif",
                transition: "background-color 0.2s, border-color 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.3)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.9)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
              }}
            >
              ?
            </button>
          )}
        </div>
      )}

      {/* Capture flash — immediate feedback that photo was taken */}
      {captured && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#fff",
            zIndex: 50,
            animation: "captureFlash 0.4s ease-out forwards",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Gate status panel */}
      {!isStupidMode && cameraReady && modelsLoaded && !captured && (() => {
        const requiredGates = [
          { key: "documentPresent", label: "Document", pass: gatingState.documentPresent },
          { key: "classified", label: "Classified", pass: gatingState.classified },
        ];
        const qualityGates = [
          { key: "size", label: "Size", pass: gatingState.size },
          { key: "centering", label: "Centered", pass: gatingState.centering },
          { key: "blur", label: "Sharpness", pass: gatingState.blur },
          { key: "glare", label: "Glare", pass: gatingState.glare },
          { key: "lighting", label: "Lighting", pass: gatingState.lighting },
        ];
        const gates = [...requiredGates, ...qualityGates];
        const yoloOk = requiredGates.every((g) => g.pass);
        const qualityPassing = qualityGates.filter((g) => g.pass).length;
        const readyToCapture = yoloOk && qualityPassing >= 3;
        return (
          <div
            style={{
              position: "absolute",
              top: 16,
              right: 12,
              zIndex: 30,
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              minWidth: 150,
            }}
          >
            {/* Header */}
            <div
              style={{
                backgroundColor: readyToCapture ? "rgba(34,139,34,0.85)" : "rgba(0,0,0,0.75)",
                borderRadius: 8,
                padding: "6px 10px",
                textAlign: "center",
                fontFamily: "monospace",
                fontSize: 12,
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "0.05em",
              }}
            >
              {readyToCapture ? "READY TO CAPTURE" : `YOLO: ${yoloOk ? "OK" : "WAIT"} | Quality: ${qualityPassing}/5 (need 3)`}
            </div>

            {/* Required YOLO gates */}
            <div style={{ fontFamily: "monospace", fontSize: 9, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.1em", paddingLeft: 4 }}>Required</div>
            {requiredGates.map((g) => (
              <div
                key={g.key}
                style={{
                  backgroundColor: g.pass ? "rgba(34,139,34,0.75)" : "rgba(180,30,30,0.75)",
                  borderRadius: 6,
                  padding: "5px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "monospace",
                  fontSize: 12,
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                <span style={{ fontSize: 14 }}>{g.pass ? "\u2705" : "\u274C"}</span>
                <span>{g.label}</span>
              </div>
            ))}

            {/* Quality gates (need 3/5) */}
            <div style={{ fontFamily: "monospace", fontSize: 9, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.1em", paddingLeft: 4 }}>Quality (need 3/5)</div>
            {qualityGates.map((g) => (
              <div
                key={g.key}
                style={{
                  backgroundColor: g.pass ? "rgba(34,139,34,0.75)" : "rgba(180,30,30,0.75)",
                  borderRadius: 6,
                  padding: "5px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "monospace",
                  fontSize: 12,
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                <span style={{ fontSize: 14 }}>{g.pass ? "\u2705" : "\u274C"}</span>
                <span>{g.label}</span>
              </div>
            ))}

            {/* YOLO / detection info */}
            <div
              style={{
                backgroundColor: "rgba(0,0,0,0.65)",
                borderRadius: 6,
                padding: "5px 10px",
                fontFamily: "monospace",
                fontSize: 10,
                color: "rgba(255,255,255,0.8)",
                lineHeight: 1.6,
              }}
            >
              <div>type: {documentType || "none"} @ {(confidence * 100).toFixed(0)}%</div>
              <div>area: {(areaRatio * 100).toFixed(1)}%</div>
              <div>model: {modelLoadError ? "none" : "YOLO"}</div>
              <div>strategy: {captureStrategy} | misses: {yoloMissCount}</div>
              {yoloDetail && (
                <>
                  <div style={{ color: yoloDetail.source === "yolo" ? "#69db7c" : "#ffd43b", fontWeight: 600, marginTop: 2 }}>
                    {yoloDetail.source} | {yoloDetail.totalDetections} det {yoloDetail.dims && `| ${yoloDetail.dims}`}
                  </div>
                  {yoloDetail.all?.map((d, i) => (
                    <div key={i} style={{ color: d.conf >= 0.12 ? "#69db7c" : "#ffa94d", paddingLeft: 6 }}>
                      #{i} {d.cls} {(d.conf * 100).toFixed(1)}% [{d.box.x},{d.box.y} {d.box.w}x{d.box.h}]
                    </div>
                  ))}
                  {yoloDetail.totalDetections === 0 && (
                    <div style={{ color: "#ff6b6b", paddingLeft: 6 }}>no detections above 12%</div>
                  )}
                  {/* Peak scores per class — shows best score across all 8400 grid cells */}
                  {yoloDetail.peaks?.length > 0 && (
                    <div style={{ marginTop: 3, borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 3 }}>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginBottom: 2 }}>PEAK SCORES (threshold: 12%)</div>
                      {yoloDetail.peaks.map((p, i) => {
                        const pct = (p.peak * 100).toFixed(1);
                        const barWidth = Math.min(p.peak * 100, 100);
                        const color = p.peak >= 0.12 ? "#69db7c" : p.peak >= 0.05 ? "#ffd43b" : "#ff6b6b";
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 1 }}>
                            <span style={{ width: 80, textAlign: "right", color }}>{p.cls}</span>
                            <div style={{ flex: 1, height: 6, backgroundColor: "rgba(255,255,255,0.1)", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: `${barWidth}%`, height: "100%", backgroundColor: color, borderRadius: 3, transition: "width 0.15s" }} />
                            </div>
                            <span style={{ width: 36, textAlign: "right", color }}>{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Branding */}
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? 54 : 32,
          left: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          pointerEvents: "none",
          zIndex: 20,
        }}
      >
        <span
          style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          {locale?.capture?.capturedBy || "Captured by"}
        </span>
        <VeratadLogo style={{ height: 22, width: "auto", opacity: 0.9 }} />
      </div>
    </div>
  );

  return ReactDOM.createPortal(portal, document.body);
}
